import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// ✅ Banner (always visible)
import HamdanBanner from "../components/About/HamdanBanner";

// ✅ Tabs content
import HamdanGroup from "../components/About/HamdanGroup";
import FoundersMessage from "../components/About/FoundersMessage";
import VisionMission from "../components/About/VisionMission";
import News from "../components/About/News";

const About: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const activeTab = searchParams.get("tab") || "group";

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch("/api/homepage/content");
        const data = await response.json();
        
        // Transform the flat database structure into the nested structure the components expect
        const formattedContent = {
          hero: {
            title: data.HeroBanners?.banner_1_title || data.Hero?.banner_1_title,
            title_ar: data.HeroBanners?.banner_1_title_ar || data.Hero?.banner_1_title_ar,
            subtitle: data.HeroBanners?.banner_1_subtitle || data.Hero?.banner_1_subtitle,
            subtitle_ar: data.HeroBanners?.banner_1_subtitle_ar || data.Hero?.banner_1_subtitle_ar,
            banner_1_image: data.HeroBanners?.banner_1,
            banner_2_image: data.HeroBanners?.banner_2,
            banner_3_image: data.HeroBanners?.banner_3,
            banner_4_image: data.HeroBanners?.banner_4,
            banner_5_image: data.HeroBanners?.banner_5,
            banner_6_image: data.HeroBanners?.banner_6,
            banner_7_image: data.HeroBanners?.banner_7,
            banner_8_image: data.HeroBanners?.banner_8,
            banner_9_image: data.HeroBanners?.banner_9,
          },
          group: {
            intro_heading: data.Hero?.intro_heading || "About • Hamdan Group",
            intro_heading_ar: data.Hero?.intro_heading_ar,
            main_title: data.Hero?.main_title || "Hamdan Trading Group",
            main_title_ar: data.Hero?.main_title_ar,
            intro: data.Hero?.intro || "Hamdan Trading Group is part of one of the largest corporate conglomerates...",
            intro_ar: data.Hero?.intro_ar,
            stats: {
              founded: data.Stats?.stat_1_value || "1975",
              stat_1_label: data.Stats?.stat_1_label || "Founded",
              stat_1_label_ar: data.Stats?.stat_1_label_ar,
              units: data.Stats?.stat_2_value || "14",
              stat_2_label: data.Stats?.stat_2_label || "Units",
              stat_2_label_ar: data.Stats?.stat_2_label_ar,
              divisions: data.Stats?.stat_3_value || "3",
              stat_3_label: data.Stats?.stat_3_label || "Divisions",
              stat_3_label_ar: data.Stats?.stat_3_label_ar,
              locations: data.Stats?.stat_4_value || "12",
              stat_4_label: data.Stats?.stat_4_label || "Strategic Locations",
              stat_4_label_ar: data.Stats?.stat_4_label_ar,
            },
            backgroundTitle: data.History?.title || "Background",
            backgroundTitle_ar: data.History?.title_ar,
            backgroundDesc: data.History?.desc || "Hamdan Trading Group is part of one of the largest corporate conglomerates...",
            backgroundDesc_ar: data.History?.desc_ar,
            csr: {
              title: data.CSR?.title || "Corporate Social Responsibility",
              title_ar: data.CSR?.title_ar,
              desc: data.CSR?.desc || "",
              desc_ar: data.CSR?.desc_ar,
              building_title: data.CSR?.building_title || "Hamdan Building — Salalah",
              building_title_ar: data.CSR?.building_title_ar,
              building_desc: data.CSR?.building_desc || "",
              building_desc_ar: data.CSR?.building_desc_ar,
              points: [
                { title: data.CSR?.point_1_title, title_ar: data.CSR?.point_1_title_ar, desc: data.CSR?.point_1_desc, desc_ar: data.CSR?.point_1_desc_ar },
                { title: data.CSR?.point_2_title, title_ar: data.CSR?.point_2_title_ar, desc: data.CSR?.point_2_desc, desc_ar: data.CSR?.point_2_desc_ar },
                { title: data.CSR?.point_3_title, title_ar: data.CSR?.point_3_title_ar, desc: data.CSR?.point_3_desc, desc_ar: data.CSR?.point_3_desc_ar },
              ]
            },
            awards: {
              title: data.Awards?.title || "Awards & Achievements",
              title_ar: data.Awards?.title_ar,
              desc: data.Awards?.desc || "",
              desc_ar: data.Awards?.desc_ar,
              points: [
                { title: data.Awards?.point_1_title, title_ar: data.Awards?.point_1_title_ar, desc: data.Awards?.point_1_desc, desc_ar: data.Awards?.point_1_desc_ar },
                { title: data.Awards?.point_2_title, title_ar: data.Awards?.point_2_title_ar, desc: data.Awards?.point_2_desc, desc_ar: data.Awards?.point_2_desc_ar },
              ]
            },
            facts: [
              { label: data.Facts?.fact_1_label || "Founded", label_ar: data.Facts?.fact_1_label_ar, value: data.Facts?.fact_1_value || "1975" },
              { label: data.Facts?.fact_2_label || "Business Units", label_ar: data.Facts?.fact_2_label_ar, value: data.Facts?.fact_2_value || "14+" },
              { label: data.Facts?.fact_3_label || "Employees", label_ar: data.Facts?.fact_3_label_ar, value: data.Facts?.fact_3_value || "300+" },
              { label: data.Facts?.fact_4_label || "Dunlop Branches", label_ar: data.Facts?.fact_4_label_ar, value: data.Facts?.fact_4_value || "12" },
            ],
            divisions: [
              { name: data.Divisions?.div_1_name || "Transport", name_ar: data.Divisions?.div_1_name_ar, desc: data.Divisions?.div_1_desc || "ISO certified", desc_ar: data.Divisions?.div_1_desc_ar },
              { name: data.Divisions?.div_2_name || "Heavy Construction", name_ar: data.Divisions?.div_2_name_ar, desc: data.Divisions?.div_2_desc || "300+ workers", desc_ar: data.Divisions?.div_2_desc_ar },
              { name: data.Divisions?.div_3_name || "Dunlop Agency", name_ar: data.Divisions?.div_3_name_ar, desc: data.Divisions?.div_3_desc || "12 branches", desc_ar: data.Divisions?.div_3_desc_ar },
            ],
            contact_cta: {
              title: data.ContactCTA?.title || "Contact Us",
              title_ar: data.ContactCTA?.title_ar,
              desc: data.ContactCTA?.desc || "Get in touch with Hamdan Trading Group for any inquiries.",
              desc_ar: data.ContactCTA?.desc_ar
            },
            image_1: data.Hero?.image_1 || "/about-hamdan-trading-group.jpg"
          },
          founder: {
            intro_title: data.Chairman?.intro_title,
            intro_title_ar: data.Chairman?.intro_title_ar,
            title: data.Chairman?.title,
            title_ar: data.Chairman?.title_ar,
            welcomeText: data.Chairman?.welcomeText,
            welcomeText_ar: data.Chairman?.welcomeText_ar,
            message: data.Chairman?.message,
            message_ar: data.Chairman?.message_ar,
            signature: {
              name: data.Chairman?.sig_name,
              name_ar: data.Chairman?.sig_name_ar,
              role: data.Chairman?.sig_role,
              role_ar: data.Chairman?.sig_role_ar
            },
            image: data.Chairman?.image
          },
          vision: {
            intro_title: data.VisionMission?.intro_title,
            intro_title_ar: data.VisionMission?.intro_title_ar,
            title: data.VisionMission?.title,
            title_ar: data.VisionMission?.title_ar,
            vision_title: data.VisionMission?.vision_title,
            vision_title_ar: data.VisionMission?.vision_title_ar,
            vision_desc: data.VisionMission?.vision_desc,
            vision_desc_ar: data.VisionMission?.vision_desc_ar,
            mission_title: data.VisionMission?.mission_title,
            mission_title_ar: data.VisionMission?.mission_title_ar,
            mission_desc: data.VisionMission?.mission_desc,
            mission_desc_ar: data.VisionMission?.mission_desc_ar,
            guiding_title: data.VisionMission?.guiding_title,
            guiding_title_ar: data.VisionMission?.guiding_title_ar,
            guiding_desc: data.VisionMission?.guiding_desc,
            guiding_desc_ar: data.VisionMission?.guiding_desc_ar,
            image_1: data.VisionMission?.image_1
          },
          news: {
            intro_title: data.News?.intro_title,
            intro_title_ar: data.News?.intro_title_ar,
            title: data.News?.title,
            title_ar: data.News?.title_ar,
            items: [1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => {
              const item = {
                id: i,
                day: data.News?.[`news_${i}_day`],
                month: data.News?.[`news_${i}_month`],
                month_ar: data.News?.[`news_${i}_month_ar`],
                year: data.News?.[`news_${i}_year`],
                title: data.News?.[`news_${i}_title`],
                title_ar: data.News?.[`news_${i}_title_ar`],
                description: data.News?.[`news_${i}_desc`],
                description_ar: data.News?.[`news_${i}_desc_ar`],
                images: [1, 2, 3, 4, 5, 6, 7, 8].map(imgIdx => data.News?.[`news_${i}_img_${imgIdx}`]).filter(Boolean)
              };
              return item.title ? item : null;
            }).filter(Boolean)
          }
        };
        
        setContent(formattedContent);
      } catch (err) {
        console.error("Failed to fetch about page content", err);
      }
    };

    fetchContent();
  }, []);

  // ✅ On tab change: scroll to top
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [activeTab]);

  return (
    <div >
      {/* ✅ Banner visible for all tabs */}
      <HamdanBanner content={content?.hero} />

      {/* ✅ Render the correct section based on header dropdown click */}
      {activeTab === "group" && <HamdanGroup content={content?.group} />}

      {activeTab === "founder" && <FoundersMessage content={content?.founder} />}

      {activeTab === "vision" && <VisionMission content={content?.vision} />}
      {activeTab === "news" && <News content={content?.news} />}
    </div>
  );
};

export default About;
